import Breadcrumb from "components/Common/Breadcrumb"
import DashboardLoader from "components/Loader/DashboardLoader"
import React, { useMemo, useState, useEffect } from "react"
import { Row, Col, Card, CardBody, Table, Badge } from "reactstrap"
import { useGetAllEarningsQuery } from "store/features/earning/earningManagementApi"
import { useGetAllCostingsQuery } from "store/features/costing/costingManagementApi"

const AllAnalytics = () => {
  const { data: earnings = [], isLoading: earningsLoading } =
    useGetAllEarningsQuery()
  const { data: costings = [], isLoading: costingsLoading } =
    useGetAllCostingsQuery()

  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7)
  ) // YYYY-MM
  const [currentDate, setCurrentDate] = useState(new Date())

  useEffect(() => {
    setCurrentDate(new Date(selectedMonth + "-01"))
  }, [selectedMonth])

  // Get previous month
  const getPreviousMonth = () => {
    const prevDate = new Date(currentDate)
    prevDate.setMonth(prevDate.getMonth() - 1)
    return prevDate.toISOString().slice(0, 7)
  }

  // Get next month (for navigation)
  const getNextMonth = () => {
    const nextDate = new Date(currentDate)
    nextDate.setMonth(nextDate.getMonth() + 1)
    return nextDate.toISOString().slice(0, 7)
  }

  // Check if next month is in the future
  const isNextMonthFuture = () => {
    const nextMonth = new Date(getNextMonth() + "-01")
    return nextMonth > new Date()
  }

  // Filter data by selected month
  const filteredData = useMemo(() => {
    const monthEarnings = earnings.filter(earning => {
      if (!earning.datesystem) return false
      const earningDate = new Date(earning.datesystem)
      return earningDate.toISOString().slice(0, 7) === selectedMonth
    })

    const monthCostings = costings.filter(costing => {
      if (!costing.datesystem) return false
      const costingDate = new Date(costing.datesystem)
      return costingDate.toISOString().slice(0, 7) === selectedMonth
    })

    return {
      earnings: monthEarnings,
      costings: monthCostings,
    }
  }, [earnings, costings, selectedMonth])

  // Calculate totals
  const totals = useMemo(() => {
    const totalEarnings = filteredData.earnings.reduce(
      (sum, earning) => sum + parseFloat(earning.amount || 0),
      0
    )

    const totalCostings = filteredData.costings.reduce(
      (sum, costing) => sum + parseFloat(costing.amount || 0),
      0
    )

    const netProfit = totalEarnings - totalCostings

    return {
      totalEarnings,
      totalCostings,
      netProfit,
    }
  }, [filteredData])

  // Format currency
  const formatCurrency = amount => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  // Format month name
  const formatMonthName = monthString => {
    const date = new Date(monthString + "-01")
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    })
  }

  // Navigation handlers
  const handlePreviousMonth = () => {
    setSelectedMonth(getPreviousMonth())
  }

  const handleNextMonth = () => {
    if (!isNextMonthFuture()) {
      setSelectedMonth(getNextMonth())
    }
  }

  const handleCurrentMonth = () => {
    setSelectedMonth(new Date().toISOString().slice(0, 7))
  }

  if (earningsLoading || costingsLoading) {
    return <DashboardLoader />
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb
          title="Financial Report"
          breadcrumbItem="Profit & Loss Statement"
        />

        {/* Month Selector */}
        <Card className="mb-4">
          <CardBody>
            <Row className="align-items-center">
              <Col md={4}>
                <h5 className="card-title mb-0">
                  {formatMonthName(selectedMonth)} Financial Report
                </h5>
              </Col>
              <Col md={8} className="text-end">
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handlePreviousMonth}
                  >
                    <i className="fas fa-chevron-left me-1"></i> Previous Month
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-success text-success"
                    onClick={handleCurrentMonth}
                  >
                    Current Month
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleNextMonth}
                    disabled={isNextMonthFuture()}
                  >
                    Next Month <i className="fas fa-chevron-right ms-1"></i>
                  </button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Row>
          {/* Earnings Section */}
          <Col lg={6}>
            <Card className="mb-4">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">Earnings</h5>
                  <Badge color="success" className="fs-6">
                    {filteredData.earnings.length} Records
                  </Badge>
                </div>

                {filteredData.earnings.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <Table className="table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Project</th>
                            <th>Client</th>
                            <th className="text-end">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredData.earnings.map(earning => (
                            <tr key={earning.id}>
                              <td>
                                <div>
                                  <h6 className="text-truncate font-size-14 m-0">
                                    {earning.project_name}
                                  </h6>
                                  {/* <small className="text-muted">
                                    {earning.notes && earning.notes.length > 30
                                      ? earning.notes.substring(0, 30) + "..."
                                      : earning.notes || "No notes"}
                                  </small> */}
                                </div>
                              </td>
                              <td>{earning.client_name}</td>
                              <td className="text-end text-success fw-semibold">
                                {formatCurrency(
                                  parseFloat(earning.amount || 0)
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="table-light">
                          <tr>
                            <td colSpan="2" className="text-end fw-bold">
                              Total Earnings:
                            </td>
                            <td className="text-end fw-bold text-success">
                              {formatCurrency(totals.totalEarnings)}
                            </td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-money-bill-wave display-4 text-muted mb-3"></i>
                    <p className="text-muted mb-0">
                      No earnings recorded for this month
                    </p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Costings Section */}
          <Col lg={6}>
            <Card className="mb-4">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">Costings</h5>
                  <Badge color="danger" className="fs-6">
                    {filteredData.costings.length} Records
                  </Badge>
                </div>

                {filteredData.costings.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <Table className="table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Project</th>
                            <th>Item</th>
                            <th className="text-end">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredData.costings.map(costing => (
                            <tr key={costing.id}>
                              <td>
                                <div>
                                  <h6 className="text-truncate font-size-14 m-0">
                                    {costing.project_name}
                                  </h6>
                                  {/* <small className="text-muted">
                                    {costing.notes && costing.notes.length > 30
                                      ? costing.notes.substring(0, 30) + "..."
                                      : costing.notes || "No notes"}
                                  </small> */}
                                </div>
                              </td>
                              <td>{costing.item_name}</td>
                              <td className="text-end text-danger fw-semibold">
                                {formatCurrency(
                                  parseFloat(costing.amount || 0)
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="table-light">
                          <tr>
                            <td colSpan="2" className="text-end fw-bold">
                              Total Costings:
                            </td>
                            <td className="text-end fw-bold text-danger">
                              {formatCurrency(totals.totalCostings)}
                            </td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-receipt display-4 text-muted mb-3"></i>
                    <p className="text-muted mb-0">
                      No costings recorded for this month
                    </p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Summary Card */}
        <Card className="mb-4">
          <CardBody>
            <h5 className="card-title mb-4">
              Financial Summary - {formatMonthName(selectedMonth)}
            </h5>
            <Row>
              <Col lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-success mb-2">
                    <i className="fas fa-arrow-up display-4"></i>
                  </div>
                  <h4 className="text-success">
                    {formatCurrency(totals.totalEarnings)}
                  </h4>
                  <p className="text-muted mb-0">Total Earnings</p>
                </div>
              </Col>
              <Col lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-danger mb-2">
                    <i className="fas fa-arrow-down display-4"></i>
                  </div>
                  <h4 className="text-danger">
                    {formatCurrency(totals.totalCostings)}
                  </h4>
                  <p className="text-muted mb-0">Total Costings</p>
                </div>
              </Col>
              <Col lg={4}>
                <div
                  className={`text-center p-3 border rounded ${
                    totals.netProfit >= 0
                      ? "bg-success bg-opacity-10"
                      : "bg-danger bg-opacity-10"
                  }`}
                >
                  <div
                    className={`mb-2 ${
                      totals.netProfit >= 0 ? "text-success" : "text-danger"
                    }`}
                  >
                    <i
                      className={`fas ${
                        totals.netProfit >= 0 ? "fa-chart-line" : "fa-chart-bar"
                      } display-4`}
                    ></i>
                  </div>
                  <h4
                    className={
                      totals.netProfit >= 0 ? "text-success" : "text-danger"
                    }
                  >
                    {formatCurrency(totals.netProfit)}
                  </h4>
                  <p className="text-muted mb-0">
                    {totals.netProfit >= 0 ? "Net Profit" : "Net Loss"}
                  </p>
                </div>
              </Col>
            </Row>

            {/* Detailed Calculation */}
            <Row className="mt-4">
              <Col lg={8} className="mx-auto">
                <div className="border rounded p-4">
                  <h6 className="mb-3">Calculation Details:</h6>
                  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <span>Total Earnings:</span>
                    <span className="text-success fw-semibold">
                      {formatCurrency(totals.totalEarnings)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <span>Total Costings:</span>
                    <span className="text-danger fw-semibold">
                      {formatCurrency(totals.totalCostings)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center py-2">
                    <span className="fw-bold">Net Result:</span>
                    <span
                      className={`fw-bold ${
                        totals.netProfit >= 0 ? "text-success" : "text-danger"
                      }`}
                    >
                      {formatCurrency(totals.netProfit)}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default AllAnalytics
